import React, { useCallback, useEffect, useState } from 'react';
import { fetchKnowledgeBase, createFAQ, updateFAQ, deleteFAQ } from '../api';
import { FAQ } from '../types';
import FAQEditor from '../components/FAQEditor';
import { useAdminLang } from '../App';
import { ADMIN_LABELS } from '../i18n';
import './KnowledgeBasePage.scss';

function getTextStr(val: any, lang = 'en'): string {
  if (!val) return '';
  if (typeof val === 'string') return val;
  return val[lang] || val['en'] || '';
}

const CATEGORY_COLORS: Record<string, string> = {
  emergency: '#c62828',
  medical: '#e65100',
  fire: '#bf360c',
  information: '#1565c0',
  safety: '#2e7d32',
};

export default function KnowledgeBasePage() {
  const { lang } = useAdminLang();
  const t = ADMIN_LABELS[lang];

  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<FAQ | null>(null);
  const [adding, setAdding] = useState(false);
  const [search, setSearch] = useState('');
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchKnowledgeBase();
      setFaqs(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (faq: Partial<FAQ>) => {
    if (editing) {
      const updated = await updateFAQ(editing.id, faq);
      setFaqs(fs => fs.map(f => f.id === editing.id ? updated : f));
      setEditing(null);
    } else {
      const created = await createFAQ(faq);
      setFaqs(fs => [...fs, created]);
      setAdding(false);
    }
    // Re-indexing is automatic on the backend
  };

  const handleDelete = async (id: string) => {
    await deleteFAQ(id);
    setFaqs(fs => fs.filter(f => f.id !== id));
    setDeleteId(null);
  };

  const filtered = faqs.filter(f => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      getTextStr(f.question, lang).toLowerCase().includes(q) ||
      getTextStr(f.answer, lang).toLowerCase().includes(q) ||
      f.intent?.toLowerCase().includes(q)
    );
  });

  return (
    <div className={`kb-page${lang === 'ar' ? ' rtl' : ''}`} dir={lang === 'ar' ? 'rtl' : 'ltr'}>
      <div className="kb-header">
        <div className="kb-title-row">
          <h2>{t.kbTitle}</h2>
          <div className="kb-actions">
            <button className="btn-primary" onClick={() => setAdding(true)}>
              {t.kbAddBtn}
            </button>
          </div>
        </div>
        <div className="kb-search">
          <input
            type="text"
            placeholder={t.kbSearch}
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <span className="kb-count">{t.kbEntries(filtered.length)}</span>
        </div>
      </div>

      <div className="kb-list">
        {loading && <div className="kb-placeholder">{t.loading}</div>}
        {!loading && filtered.length === 0 && (
          <div className="kb-placeholder">{t.kbNoItems}</div>
        )}
        {filtered.map(faq => (
          <div key={faq.id} className="faq-row">
            <div className="faq-content">
              <div className="faq-q">
                <span className="faq-label">Q</span>
                <span>{getTextStr(faq.question, lang)}</span>
              </div>
              <div className="faq-a">
                <span className="faq-label">A</span>
                <span>{getTextStr(faq.answer, lang)}</span>
              </div>
              {faq.intent && (
                <span
                  className="faq-intent"
                  style={{ background: CATEGORY_COLORS[faq.intent] || '#555' }}
                >
                  {faq.intent}
                </span>
              )}
            </div>
            <div className="faq-actions">
              <button className="btn-icon" onClick={() => setEditing(faq)} title="Edit">✏️</button>
              <button className="btn-icon btn-icon-delete" onClick={() => setDeleteId(faq.id)} title="Delete">🗑️</button>
            </div>
          </div>
        ))}
      </div>

      {/* Add / Edit modal */}
      {(adding || editing) && (
        <div className="modal-overlay" onClick={() => { setAdding(false); setEditing(null); }}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <FAQEditor
              faq={editing || undefined}
              onSave={handleSave}
              onCancel={() => { setAdding(false); setEditing(null); }}
            />
          </div>
        </div>
      )}

      {/* Delete confirm modal */}
      {deleteId && (
        <div className="modal-overlay" onClick={() => setDeleteId(null)}>
          <div className="modal-box small" onClick={e => e.stopPropagation()}>
            <h3>{t.deleteConfirmTitle}</h3>
            <p>{t.deleteConfirmBody}</p>
            <div className="modal-btns">
              <button className="btn-secondary" onClick={() => setDeleteId(null)}>{t.cancel}</button>
              <button className="btn-danger" onClick={() => handleDelete(deleteId)}>{t.deleteBtn}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
